// Route-level SEO + Open Graph metadata (SM-381).
//
// Two consumers read this module, and they cover different audiences:
//
//   1. TanStack Router's `head` option on every route, rendered into the
//      live document by <HeadContent /> in __root.tsx. This is what a
//      browser tab title shows, what a JS-executing crawler reads, and
//      what an in-app share sheet picks up.
//
//   2. Nothing else — because web-client is a static SPA build, tags
//      produced here exist only after the bundle runs. Facebook's,
//      LINE's and Slack's crawlers do not execute JavaScript, so for
//      THEM the metadata is served upstream: index.html carries the
//      site-wide defaults, and web-client/nginx.conf routes crawler
//      user-agents on /markets/:id and /explore/:id to the gateway's
//      /api/og/markets/{id} document, which renders the market-specific
//      tags. See api-gateway/internal/controller/client/og.go.
//
// Market-detail crawler metadata remains owned by that Go file; changes to
// that separately deployed surface must be mirrored there.

import { BRAND_OG_IMAGES, OG_IMAGE_WIDTH, OG_IMAGE_HEIGHT } from "@/brand/ogImages";
import { BRAND_METADATA } from "@/brand/metadata";
import { resolveBrandId } from "@/brand/registry";

// The brand this build was configured for. Same resolution the rest of the
// app uses: an unset or unknown VITE_BRAND lands on the default brand rather
// than on a missing image.
const configuredBrandId = () => resolveBrandId(import.meta.env.VITE_BRAND);
const configuredBrandMetadata = BRAND_METADATA[configuredBrandId()];

export const SITE = {
  ...configuredBrandMetadata,
  // Fallback preview image — the ACTIVE BRAND's card (SM-334). 1200x630, the
  // 1.91:1 ratio Facebook, X and LINE render as a large card. Resolved from
  // src/brand/ogImages.ts, which is pure data, so this module stays free of
  // React and vite.config.ts can inline the same value into index.html.
  image: BRAND_OG_IMAGES[configuredBrandId()] ?? BRAND_OG_IMAGES.smartmarket,
  imageWidth: OG_IMAGE_WIDTH,
  imageHeight: OG_IMAGE_HEIGHT,
} as const;

// TITLE_SUFFIX brands every page except the home page, which already
// carries the full tagline.
const TITLE_SUFFIX = ` — ${SITE.name}`;

// Facebook renders ~88 chars of title and ~300 of description;
// Twitter/X cuts descriptions at ~200. One text has to satisfy both, so
// 200 is the ceiling.
const TITLE_LIMIT = 88;
const DESCRIPTION_LIMIT = 200;

export interface MetaInput {
  /** Page title WITHOUT the site-name suffix. Omit for the site default. */
  title?: string;
  description?: string;
  /** Route path, e.g. "/markets/abc". Resolved to an absolute canonical URL. */
  path?: string;
  /** Absolute or root-relative image URL. Falls back to SITE.image. */
  image?: string;
  /** Alt text for the preview image. */
  imageAlt?: string;
  /** og:type — "website" for everything we currently ship. */
  type?: string;
  /**
   * Keep the path out of search indexes. Set on signed-in-only surfaces
   * (portfolio, wallet, settings) and on the dev-only preview routes:
   * they still deserve a real tab title, but they are not content.
   */
  noindex?: boolean;
  /**
   * Emit a <link rel="canonical">. Defaults to true; the ROOT route
   * passes false.
   *
   * Why the exception: TanStack dedupes `meta` by name/property, so a
   * child's og:title replaces the root's — but it does NOT dedupe
   * `links`. With the root emitting one too, every page carried two
   * canonicals ("/" from the root, then the real path), and the root's
   * came first, which tells a crawler the whole site is one page. Only
   * the matched leaf route knows the canonical path, so only it emits
   * the tag.
   */
  canonical?: boolean;
}

/** One <meta>/<title>/<link> descriptor, in the shape TanStack expects. */
type MetaTag = Record<string, string>;

export interface HeadDescriptor {
  meta: MetaTag[];
  links: MetaTag[];
}

/**
 * origin resolves the site's public origin.
 *
 * window.location.origin is correct in every real environment (local,
 * UAT, prod) and needs no per-environment config: the page was served
 * from the origin we want links to point at. The "" fallback keeps this
 * module callable under vitest's node environment, where the absence of
 * `window` would otherwise throw during route-module evaluation.
 */
function origin(): string {
  if (typeof window === "undefined") return "";
  return window.location.origin;
}

/**
 * absolute turns a root-relative path into a full URL. og:image and
 * og:url MUST be absolute — a crawler fetches them with no page context,
 * so a relative value yields no preview image at all.
 */
export function absolute(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  if (pathOrUrl.startsWith("//")) return pathOrUrl;
  const base = origin();
  const path = pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`;
  return base ? base + path : path;
}

/**
 * clean flattens catalog prose into a single line. Catalog text is
 * authored in a rich-text field, so it can arrive with tags in it, and
 * literal "<p>" in a card title is exactly the kind of thing that ships
 * unnoticed.
 */
export function clean(s: string | undefined | null): string {
  if (!s) return "";
  return s
    .replace(/<[^>]*>/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * truncate cuts to at most `limit` CHARACTERS, preferring a word
 * boundary when one is near the end. Thai has no inter-word spaces —
 * the common case for this catalog — so a hard cut is correct there and
 * the boundary search is deliberately conditional.
 */
export function truncate(s: string, limit: number): string {
  const chars = [...s];
  if (chars.length <= limit) return s;
  let cut = chars.slice(0, limit).join("");
  const space = cut.lastIndexOf(" ");
  if (space > limit * 0.75) cut = cut.slice(0, space);
  return cut.replace(/[\s,;:—-]+$/, "") + "…";
}

/**
 * buildHead produces the `head` value for a route: the full SEO + Open
 * Graph + Twitter card tag set, with site-wide fallbacks filled in for
 * anything the route didn't supply.
 *
 * Every route gets the complete set rather than a partial one, because
 * TanStack merges head descriptors by tag identity down the route tree —
 * a child that omits og:image would otherwise inherit its parent's,
 * which on a market page is exactly the generic-preview bug SM-381 is
 * about.
 */
export function buildHead(input: MetaInput = {}): HeadDescriptor {
  const title = input.title
    ? truncate(clean(input.title), TITLE_LIMIT) + TITLE_SUFFIX
    : SITE.tagline;
  const description = truncate(clean(input.description) || SITE.description, DESCRIPTION_LIMIT);
  const url = absolute(input.path ?? "/");
  const image = absolute(input.image || SITE.image);
  const imageAlt = clean(input.imageAlt) || title;
  const isDefaultImage = image === absolute(SITE.image);

  const meta: MetaTag[] = [
    { title },
    { name: "description", content: description },

    { property: "og:site_name", content: SITE.name },
    { property: "og:type", content: input.type ?? "website" },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:url", content: url },
    { property: "og:image", content: image },
    { property: "og:image:secure_url", content: image },
    { property: "og:image:alt", content: imageAlt },

    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: image },
    { name: "twitter:image:alt", content: imageAlt },
  ];

  // Dimensions are declared only for our own asset. A market icon is
  // operator-uploaded at an unknown size, and a wrong dimension hint is
  // worse than none — crawlers trust the tag over the bytes.
  if (isDefaultImage) {
    meta.push(
      { property: "og:image:width", content: SITE.imageWidth },
      { property: "og:image:height", content: SITE.imageHeight },
    );
  }

  if (input.noindex) {
    meta.push({ name: "robots", content: "noindex, nofollow" });
  }

  const links: MetaTag[] = input.canonical === false ? [] : [{ rel: "canonical", href: url }];
  return { meta, links };
}

/**
 * dropStaticMeta removes the site-wide SEO/OG block that index.html ships
 * for non-JS crawlers, and is called once at boot (src/main.tsx) before
 * the router renders.
 *
 * Without this, the two systems stack: index.html's generic tags stay in
 * <head> and <HeadContent /> appends the route's on top, leaving two
 * og:title tags per page with the GENERIC one first — and a consumer that
 * reads the first occurrence (which is the norm) would see exactly the
 * preview SM-381 is about, on every page. The static block is strictly a
 * no-JavaScript fallback, so the moment JavaScript runs it has done its
 * job and must get out of the way.
 *
 * Matched by the data-og-static attribute rather than by tag name, so
 * hand-written tags in index.html (viewport, theme-color, icon) are left
 * alone.
 */
export function dropStaticMeta(doc: Document = document): number {
  const stale = doc.head.querySelectorAll("[data-og-static]");
  stale.forEach((el) => el.remove());
  return stale.length;
}

/**
 * marketHead builds the metadata for a market-detail page from its
 * catalog row. This is the case the card was filed about: a shared
 * /markets/:id link must preview with that market's own question and
 * picture, not the site tagline.
 *
 * Written to accept a partial/absent row so it can be called before the
 * catalog read resolves (or when it fails) and simply degrade to the
 * site defaults.
 */
export interface MarketMetaSource {
  name?: string;
  event_name?: string;
  description?: string;
  market_context?: string;
  rules?: string;
  icon_url?: string;
}

export function marketHead(path: string, m: MarketMetaSource | null | undefined): HeadDescriptor {
  if (!m?.name) return buildHead({ path });

  const name = clean(m.name);
  const event = clean(m.event_name);
  // Append the parent event only when it adds context the question
  // lacks — repeating it just burns the title budget.
  const title =
    event && event.toLowerCase() !== name.toLowerCase() && !name.toLowerCase().includes(event.toLowerCase())
      ? `${name} — ${event}`
      : name;

  // description (overview) → market_context (background) → rules
  // (resolution criteria): descending order of usefulness to someone
  // deciding whether to open a shared link.
  const description = clean(m.description) || clean(m.market_context) || clean(m.rules);

  return buildHead({
    path,
    title,
    description,
    image: m.icon_url,
    imageAlt: name,
  });
}
